import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import { FiSave, FiZap, FiRefreshCw } from 'react-icons/fi';
import LoadingButton from './LoadingButton';
import { queryKeys } from '../config/queryKeys';
import {
  AI_PROVIDER_LABELS,
  AI_ACTION_KEYS,
  AI_ACTION_LABELS,
  defaultProvidersEnabled,
  defaultActionProviders,
  getEnabledProviderIds,
} from '../config/aiProviders';
import { useAtsMatchRun } from '../context/AtsMatchRunContext';
import { summarizeAiMatchResults } from '../utils/aiMatchMessages';
import { normalizePagedList } from '../utils/listPagination';
import { appAlert, appConfirm } from '../utils/appDialog';

function resolveActionProviderValue(actionProviders, actionKey, enabledIds) {
  const chosen = actionProviders?.[actionKey];
  if (enabledIds.includes(chosen)) return chosen;
  return enabledIds[0] || 'openai';
}

function mergeSavedAiConfig(saved, submitted) {
  if (!saved) return submitted || null;
  const providers_enabled = {
    openai: false,
    anthropic: false,
    gemini: false,
    custom: false,
    ...(saved.providers_enabled || {}),
    ...(submitted?.providers_enabled || {}),
  };
  const enabledIds = getEnabledProviderIds(providers_enabled);
  const action_providers = { ...defaultActionProviders(), ...(saved.action_providers || {}) };
  for (const actionKey of AI_ACTION_KEYS) {
    action_providers[actionKey] = resolveActionProviderValue(action_providers, actionKey, enabledIds);
  }
  return {
    ...saved,
    providers_enabled,
    action_providers,
    custom_host: saved.custom_host || submitted?.custom_host || '',
    custom_client_id: saved.custom_client_id || submitted?.custom_client_id || '',
    custom_secret_set: saved.custom_secret_set || Boolean(submitted?.custom_client_secret),
    match_logic: saved.match_logic || submitted?.match_logic || null,
    match_catalog: saved.match_catalog || null,
    min_match_score: saved.min_match_score ?? submitted?.min_match_score,
  };
}

function normalizeHost(host) {
  const trimmed = String(host || '').trim();
  if (!trimmed) return '';
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return withScheme.replace(/\/+$/, '');
}

function ProviderSectionTitle({ id, label, enabled, onToggle }) {
  return (
    <h3 style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
        {label}
      </label>
      {!enabled && <span style={{ fontSize: 13, fontWeight: 'normal', color: '#888' }}>(disabled)</span>}
    </h3>
  );
}

const AiMatchingSettings = () => {
  const queryClient = useQueryClient();
  const { startJobMatch } = useAtsMatchRun();
  const [form, setForm] = useState({
    providers_enabled: defaultProvidersEnabled(),
    action_providers: defaultActionProviders(),
    openai_model: 'gpt-4o-mini',
    openai_api_key: '',
    anthropic_model: 'claude-3-5-sonnet-20241022',
    anthropic_api_key: '',
    gemini_model: 'gemini-1.5-flash',
    gemini_api_key: '',
    custom_host: '',
    custom_client_id: '',
    custom_client_secret: '',
    custom_model: 'gpt-4o-mini',
    min_match_score: 35,
    match_logic: {
      engine: 'ai',
      hybrid_ai_weight: 70,
      hybrid_rules_weight: 30,
      require_classification_match: false,
      job_fields: {},
      candidate_fields: {},
      criteria: [],
      ai_prompt_focus: '',
    },
  });
  const [catalog, setCatalog] = useState(null);
  const [recomputeBusy, setRecomputeBusy] = useState(false);
  const [recomputeLog, setRecomputeLog] = useState('');
  const [saveError, setSaveError] = useState('');
  const [probeBusy, setProbeBusy] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const formDirtyRef = useRef(false);

  const applyServerConfig = useCallback((saved) => {
    if (!saved) return;
    const providers_enabled = {
      openai: false,
      anthropic: false,
      gemini: false,
      custom: false,
      ...(saved.providers_enabled || {}),
    };
    const enabledIds = getEnabledProviderIds(providers_enabled);
    const action_providers = { ...defaultActionProviders(), ...(saved.action_providers || {}) };
    for (const actionKey of AI_ACTION_KEYS) {
      action_providers[actionKey] = resolveActionProviderValue(action_providers, actionKey, enabledIds);
    }
    if (saved.match_catalog) setCatalog(saved.match_catalog);
    setForm((prev) => ({
      ...prev,
      providers_enabled,
      action_providers,
      openai_model: saved.openai_model || prev.openai_model,
      anthropic_model: saved.anthropic_model || prev.anthropic_model,
      gemini_model: saved.gemini_model || prev.gemini_model,
      custom_host: saved.custom_host || '',
      custom_client_id: saved.custom_client_id || '',
      custom_model: saved.custom_model || prev.custom_model,
      min_match_score: saved.min_match_score ?? 35,
      match_logic: {
        ...prev.match_logic,
        ...(saved.match_logic || {}),
        job_fields: { ...(prev.match_logic?.job_fields || {}), ...(saved.match_logic?.job_fields || {}) },
        candidate_fields: {
          ...(prev.match_logic?.candidate_fields || {}),
          ...(saved.match_logic?.candidate_fields || {}),
        },
        criteria: Array.isArray(saved.match_logic?.criteria)
          ? saved.match_logic.criteria
          : prev.match_logic?.criteria || [],
      },
      openai_api_key: '',
      anthropic_api_key: '',
      gemini_api_key: '',
      custom_client_secret: '',
    }));
  }, []);

  const patchForm = useCallback((patch) => {
    formDirtyRef.current = true;
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const { data, isLoading, isError, error } = useQuery(
    ['settings-ai-matching'],
    () => api.get('/permissions/ai-matching').then((r) => r.data),
    { refetchOnWindowFocus: false, retry: false }
  );

  const serverConfig = data ?? queryClient.getQueryData(['settings-ai-matching']);

  const enabledProviderIds = useMemo(
    () => getEnabledProviderIds(form.providers_enabled),
    [form.providers_enabled]
  );

  useEffect(() => {
    if (!data || formDirtyRef.current) return;
    applyServerConfig(data);
  }, [data, applyServerConfig]);

  const setProviderEnabled = (providerId, enabled) => {
    formDirtyRef.current = true;
    setForm((prev) => {
      const providers_enabled = { ...prev.providers_enabled, [providerId]: enabled };
      const enabledIds = getEnabledProviderIds(providers_enabled);
      const action_providers = { ...prev.action_providers };
      for (const actionKey of AI_ACTION_KEYS) {
        if (!enabledIds.includes(action_providers[actionKey])) {
          action_providers[actionKey] = enabledIds[0] || 'openai';
        }
      }
      return { ...prev, providers_enabled, action_providers };
    });
  };

  const setActionProvider = (actionKey, providerId) => {
    formDirtyRef.current = true;
    setForm((prev) => {
      const action_providers = { ...prev.action_providers, [actionKey]: providerId };
      if (actionKey === 'job_rematch') action_providers.resume_studio = providerId;
      return { ...prev, action_providers };
    });
  };

  const saveMutation = useMutation(
    (body) => api.put('/permissions/ai-matching', body).then((r) => r.data),
    {
      onSuccess: (saved, variables) => {
        const merged = mergeSavedAiConfig(saved, variables);
        formDirtyRef.current = false;
        setSaveError('');
        queryClient.setQueryData(['settings-ai-matching'], merged);
        applyServerConfig(merged);
        void appAlert('ATS match settings saved.');
      },
      onError: (e) => {
        const msg = e.response?.data?.details || e.response?.data?.error || e.message;
        setSaveError(msg);
        void appAlert(`Failed to save ATS match settings: ${msg}`);
      },
    }
  );

  const handleSave = (e) => {
    e.preventDefault();
    setSaveError('');

    if (!enabledProviderIds.length) {
      setSaveError('Enable at least one AI provider.');
      void appAlert('Enable at least one AI provider.');
      return;
    }

    const customHost = normalizeHost(form.custom_host);
    const customSecretKnown =
      Boolean(serverConfig?.custom_secret_set) || Boolean(queryClient.getQueryData(['settings-ai-matching'])?.custom_secret_set);

    if (form.providers_enabled.custom) {
      if (!customHost) {
        setSaveError('Custom AI Host is required when custom provider is enabled.');
        void appAlert('Custom AI Host is required when custom provider is enabled.');
        return;
      }
      if (!form.custom_client_id.trim()) {
        setSaveError('Client Id is required when custom provider is enabled.');
        void appAlert('Client Id is required when custom provider is enabled.');
        return;
      }
      if (!form.custom_client_secret.trim() && !customSecretKnown) {
        setSaveError('Client Secret is required when enabling custom provider for the first time.');
        void appAlert('Client Secret is required when enabling custom provider for the first time.');
        return;
      }
    }

    const action_providers = { ...form.action_providers };
    action_providers.resume_studio = action_providers.job_rematch;
    for (const actionKey of AI_ACTION_KEYS) {
      action_providers[actionKey] = resolveActionProviderValue(
        action_providers,
        actionKey,
        enabledProviderIds
      );
    }

    for (const actionKey of AI_ACTION_KEYS) {
      if (!enabledProviderIds.includes(action_providers[actionKey])) {
        void appAlert(`Select an enabled provider for ${AI_ACTION_LABELS[actionKey]}.`);
        return;
      }
    }

    const body = {
      providers_enabled: {
        openai: Boolean(form.providers_enabled.openai),
        anthropic: Boolean(form.providers_enabled.anthropic),
        gemini: Boolean(form.providers_enabled.gemini),
        custom: Boolean(form.providers_enabled.custom),
      },
      action_providers,
      openai_model: form.openai_model,
      anthropic_model: form.anthropic_model,
      gemini_model: form.gemini_model,
      custom_host: customHost,
      custom_client_id: form.custom_client_id.trim(),
      custom_model: form.custom_model,
      min_match_score: form.min_match_score,
      match_logic: form.match_logic,
    };
    if (form.openai_api_key.trim()) body.openai_api_key = form.openai_api_key.trim();
    if (form.anthropic_api_key.trim()) body.anthropic_api_key = form.anthropic_api_key.trim();
    if (form.gemini_api_key.trim()) body.gemini_api_key = form.gemini_api_key.trim();
    if (form.custom_client_secret.trim()) body.custom_client_secret = form.custom_client_secret.trim();
    saveMutation.mutate(body);
  };

  const runRecomputeAll = async () => {
    if (!await appConfirm('Run AI matching for every active job? This calls your AI provider once per candidate per job and may take several minutes.')) return;
    setRecomputeBusy(true);
    setRecomputeLog('');
    try {
      const jobsRes = await api.get('/jobs', { params: { limit: 500, page: 1 } });
      const { items: firstPage, pagination } = normalizePagedList(jobsRes.data, { itemsKey: 'jobs' });
      let jobs = [...firstPage];
      const pageCount = pagination?.pageCount || 1;
      for (let p = 2; p <= pageCount; p += 1) {
        const next = await api.get('/jobs', { params: { limit: 500, page: p } });
        jobs = jobs.concat(normalizePagedList(next.data, { itemsKey: 'jobs' }).items);
      }
      jobs = jobs.filter((j) => j.status === 'active');
      const lines = [];
      for (const job of jobs) {
        try {
          const r = await startJobMatch(job.id, { label: job.title, notify: false });
          lines.push(`Job #${job.id} ${job.title}: ${summarizeAiMatchResults(r.data).replace(/\n/g, ' ')}`);
        } catch (err) {
          lines.push(`Job #${job.id}: ${err.response?.data?.error || err.message}`);
        }
      }
      setRecomputeLog(lines.join('\n'));
      queryClient.invalidateQueries(queryKeys.jobsRoot);
      queryClient.invalidateQueries('all-matches');
    } finally {
      setRecomputeBusy(false);
    }
  };

  const runCustomAiProbe = async () => {
    setProbeBusy(true);
    setProbeResult(null);
    try {
      const { data } = await api.post('/permissions/ai-matching/test-custom');
      setProbeResult(data);
    } catch (err) {
      const data = err.response?.data;
      setProbeResult(
        data && typeof data === 'object'
          ? data
          : { ok: false, hint: err.response?.data?.error || err.message || 'Probe failed' }
      );
    } finally {
      setProbeBusy(false);
    }
  };

  const patchMatchLogic = (patch) => {
    formDirtyRef.current = true;
    setForm((prev) => ({
      ...prev,
      match_logic: { ...prev.match_logic, ...patch },
    }));
  };

  const toggleJobField = (fieldId, enabled) => {
    formDirtyRef.current = true;
    setForm((prev) => ({
      ...prev,
      match_logic: {
        ...prev.match_logic,
        job_fields: { ...prev.match_logic.job_fields, [fieldId]: enabled },
      },
    }));
  };

  const toggleCandidateField = (fieldId, enabled) => {
    formDirtyRef.current = true;
    setForm((prev) => ({
      ...prev,
      match_logic: {
        ...prev.match_logic,
        candidate_fields: { ...prev.match_logic.candidate_fields, [fieldId]: enabled },
      },
    }));
  };

  const patchCriterion = (criterionId, patch) => {
    formDirtyRef.current = true;
    setForm((prev) => ({
      ...prev,
      match_logic: {
        ...prev.match_logic,
        criteria: (prev.match_logic.criteria || []).map((c) =>
          c.id === criterionId ? { ...c, ...patch } : c
        ),
      },
    }));
  };

  if (isLoading) return <div className="loading">Loading ATS match settings…</div>;

  const loadError = isError
    ? error?.response?.data?.error || error?.message || 'Failed to load ATS match settings'
    : '';

  const jobFieldCatalog = catalog?.job_fields || [];
  const candidateFieldCatalog = catalog?.candidate_fields || [];
  const criteriaCatalog = catalog?.criteria || [];
  const engineOptions = catalog?.engines || [
    { id: 'ai', label: 'AI only' },
    { id: 'rules', label: 'Rules only' },
    { id: 'hybrid', label: 'Hybrid (AI + rules)' },
  ];
  const criteriaMeta = Object.fromEntries(criteriaCatalog.map((c) => [c.id, c]));

  return (
    <div className="settings-section">
      <h2>
        <FiZap /> ATS match
      </h2>
      {loadError && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          Could not load saved settings ({loadError}). You can still edit and save below.
        </div>
      )}
      {saveError && (
        <div className="alert alert-danger" style={{ marginBottom: 16 }}>
          {saveError}
        </div>
      )}
      <p className="form-hint" style={{ marginTop: 0, marginBottom: 16 }}>
        ATS match scores each candidate profile against a job using your AI provider (full profile:
        experience, education, skills, certifications — not skills-only). Configure providers below,
        then set field inclusion and optional hybrid/rules weights.
      </p>

      <form onSubmit={handleSave} className="settings-form">
        <h3 style={{ marginTop: 0 }}>Match pipeline</h3>
        <div className="form-group">
          <label htmlFor="ats-match-engine">Scoring engine</label>
          <select
            id="ats-match-engine"
            value={form.match_logic.engine || 'ai'}
            onChange={(e) => patchMatchLogic({ engine: e.target.value })}
          >
            {engineOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <small>
            AI uses your configured provider. Rules use weighted criteria below. Hybrid blends both.
          </small>
        </div>

        {form.match_logic.engine === 'hybrid' && (
          <div className="svc-form-grid svc-form-grid--2" style={{ marginBottom: 12 }}>
            <div className="form-group">
              <label htmlFor="ats-hybrid-ai">AI weight</label>
              <input
                id="ats-hybrid-ai"
                type="number"
                min={0}
                max={100}
                value={form.match_logic.hybrid_ai_weight ?? 70}
                onChange={(e) =>
                  patchMatchLogic({ hybrid_ai_weight: parseInt(e.target.value, 10) || 0 })
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="ats-hybrid-rules">Rules weight</label>
              <input
                id="ats-hybrid-rules"
                type="number"
                min={0}
                max={100}
                value={form.match_logic.hybrid_rules_weight ?? 30}
                onChange={(e) =>
                  patchMatchLogic({ hybrid_rules_weight: parseInt(e.target.value, 10) || 0 })
                }
              />
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="ats-min-score">Minimum match score (0–100)</label>
          <input
            id="ats-min-score"
            type="number"
            min={0}
            max={100}
            value={form.min_match_score}
            onChange={(e) => patchForm({ min_match_score: parseInt(e.target.value, 10) || 0 })}
          />
          <small>Pairs below this threshold are removed from the ATS match pipeline.</small>
        </div>

        <label className="svc-check" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={Boolean(form.match_logic.require_classification_match)}
            onChange={(e) => patchMatchLogic({ require_classification_match: e.target.checked })}
          />
          Require matching job classification (hard filter before scoring)
        </label>

        <div className="form-group">
          <label htmlFor="ats-prompt-focus">AI focus notes (optional)</label>
          <textarea
            id="ats-prompt-focus"
            rows={2}
            value={form.match_logic.ai_prompt_focus || ''}
            onChange={(e) => patchMatchLogic({ ai_prompt_focus: e.target.value })}
            placeholder="e.g. Prefer cloud security experience over general IT"
          />
          <small>Injected into the AI prompt when the engine is AI or hybrid.</small>
        </div>

        <h3>Job fields to include</h3>
        <p className="form-hint">Only enabled fields are sent to the AI / used as job-side inputs.</p>
        <div className="ats-match-field-grid">
          {jobFieldCatalog.map((f) => (
            <label key={f.id} className="svc-check">
              <input
                type="checkbox"
                checked={form.match_logic.job_fields?.[f.id] !== false}
                onChange={(e) => toggleJobField(f.id, e.target.checked)}
              />
              {f.label}
            </label>
          ))}
        </div>

        <h3>Candidate profile fields to include</h3>
        <p className="form-hint">Control what from the candidate profile and resume is compared.</p>
        <div className="ats-match-field-grid">
          {candidateFieldCatalog.map((f) => (
            <label key={f.id} className="svc-check">
              <input
                type="checkbox"
                checked={form.match_logic.candidate_fields?.[f.id] !== false}
                onChange={(e) => toggleCandidateField(f.id, e.target.checked)}
              />
              {f.label}
            </label>
          ))}
        </div>

        <h3>Weighted criteria</h3>
        <p className="form-hint">
          Enable criteria and set relative weights (0–100). Used by rules / hybrid scoring and hinted to
          AI.
        </p>
        <div className="erp-table-scroll" style={{ marginBottom: 16 }}>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th scope="col">On</th>
                <th scope="col">Criterion</th>
                <th scope="col">Job ↔ Candidate</th>
                <th scope="col">Weight</th>
              </tr>
            </thead>
            <tbody>
              {(form.match_logic.criteria || []).map((c) => {
                const meta = criteriaMeta[c.id] || {};
                return (
                  <tr key={c.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(c.enabled)}
                        onChange={(e) => patchCriterion(c.id, { enabled: e.target.checked })}
                        aria-label={`Enable ${meta.label || c.id}`}
                      />
                    </td>
                    <td>
                      <strong>{meta.label || c.id}</strong>
                      {meta.hard_filter ? (
                        <div className="form-hint" style={{ margin: 0 }}>
                          Also available as hard filter above
                        </div>
                      ) : null}
                    </td>
                    <td className="form-hint">
                      {meta.job_field || '—'} ↔ {meta.candidate_field || '—'}
                    </td>
                    <td style={{ width: 100 }}>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={c.weight ?? 0}
                        disabled={!c.enabled}
                        onChange={(e) =>
                          patchCriterion(c.id, { weight: parseInt(e.target.value, 10) || 0 })
                        }
                        aria-label={`Weight for ${meta.label || c.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h3>AI providers</h3>
        <p className="form-hint" style={{ marginBottom: 16 }}>
          Enable providers, configure credentials, then choose which provider powers each product action.
        </p>
        <ProviderSectionTitle
          id="openai"
          label="OpenAI"
          enabled={form.providers_enabled.openai}
          onToggle={(checked) => setProviderEnabled('openai', checked)}
        />
        <fieldset disabled={!form.providers_enabled.openai} style={{ border: 'none', margin: 0, padding: 0, opacity: form.providers_enabled.openai ? 1 : 0.65 }}>
          <div className="form-group">
            <label>Model</label>
            <input
              type="text"
              value={form.openai_model}
              onChange={(e) => setForm({ ...form, openai_model: e.target.value })}
              placeholder="gpt-4o-mini"
            />
          </div>
          <div className="form-group">
            <label>API key {serverConfig?.openai_key_set && <span style={{ color: '#666' }}>(saved: {serverConfig.openai_key_preview})</span>}</label>
            <input
              type="password"
              autoComplete="off"
              value={form.openai_api_key}
              onChange={(e) => setForm({ ...form, openai_api_key: e.target.value })}
              placeholder={serverConfig?.openai_key_set ? 'Leave blank to keep existing' : 'sk-...'}
            />
          </div>
        </fieldset>

        <ProviderSectionTitle
          id="anthropic"
          label="Anthropic"
          enabled={form.providers_enabled.anthropic}
          onToggle={(checked) => setProviderEnabled('anthropic', checked)}
        />
        <fieldset disabled={!form.providers_enabled.anthropic} style={{ border: 'none', margin: 0, padding: 0, opacity: form.providers_enabled.anthropic ? 1 : 0.65 }}>
          <div className="form-group">
            <label>Model</label>
            <input
              type="text"
              value={form.anthropic_model}
              onChange={(e) => setForm({ ...form, anthropic_model: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>API key {serverConfig?.anthropic_key_set && <span style={{ color: '#666' }}>(saved: {serverConfig.anthropic_key_preview})</span>}</label>
            <input
              type="password"
              autoComplete="off"
              value={form.anthropic_api_key}
              onChange={(e) => setForm({ ...form, anthropic_api_key: e.target.value })}
              placeholder={serverConfig?.anthropic_key_set ? 'Leave blank to keep existing' : 'sk-ant-...'}
            />
          </div>
        </fieldset>

        <ProviderSectionTitle
          id="gemini"
          label="Google Gemini"
          enabled={form.providers_enabled.gemini}
          onToggle={(checked) => setProviderEnabled('gemini', checked)}
        />
        <fieldset disabled={!form.providers_enabled.gemini} style={{ border: 'none', margin: 0, padding: 0, opacity: form.providers_enabled.gemini ? 1 : 0.65 }}>
          <div className="form-group">
            <label>Model id</label>
            <input
              type="text"
              value={form.gemini_model}
              onChange={(e) => setForm({ ...form, gemini_model: e.target.value })}
              placeholder="gemini-1.5-flash"
            />
          </div>
          <div className="form-group">
            <label>API key {serverConfig?.gemini_key_set && <span style={{ color: '#666' }}>(saved: {serverConfig.gemini_key_preview})</span>}</label>
            <input
              type="password"
              autoComplete="off"
              value={form.gemini_api_key}
              onChange={(e) => setForm({ ...form, gemini_api_key: e.target.value })}
              placeholder={serverConfig?.gemini_key_set ? 'Leave blank to keep existing' : 'AIza...'}
            />
          </div>
        </fieldset>

        <ProviderSectionTitle
          id="custom"
          label="Custom AI Gateway"
          enabled={form.providers_enabled.custom}
          onToggle={(checked) => setProviderEnabled('custom', checked)}
        />
        <fieldset disabled={!form.providers_enabled.custom} style={{ border: 'none', margin: 0, padding: 0, opacity: form.providers_enabled.custom ? 1 : 0.65 }}>
          <p style={{ color: '#666', marginBottom: 12, fontSize: 14 }}>
            Your AI Gateway behind Cloudflare Access. Auth uses CF-Access-Client-Id and CF-Access-Client-Secret.
            Calls POST /v1/text/generate on the host below (prompt + model_id).
          </p>
          <div className="form-group">
            <label>Custom AI Host (base URL)</label>
            <input
              type="text"
              inputMode="url"
              value={form.custom_host}
              onChange={(e) => patchForm({ custom_host: e.target.value })}
              placeholder="https://ai-gateway.example.com"
            />
            <small>Gateway base URL only (no path). Include https:// or enter hostname — we add https:// on save.</small>
          </div>
          <div className="form-group">
            <label>Client Id</label>
            <input
              type="text"
              autoComplete="off"
              value={form.custom_client_id}
              onChange={(e) => patchForm({ custom_client_id: e.target.value })}
              placeholder="your-client-id"
            />
          </div>
          <div className="form-group">
            <label>
              Client Secret{' '}
              {serverConfig?.custom_secret_set && (
                <span style={{ color: '#666' }}>(saved: {serverConfig.custom_secret_preview})</span>
              )}
            </label>
            <input
              type="password"
              autoComplete="off"
              value={form.custom_client_secret}
              onChange={(e) => patchForm({ custom_client_secret: e.target.value })}
              placeholder={serverConfig?.custom_secret_set ? 'Leave blank to keep existing' : 'your-client-secret'}
            />
          </div>
          <div className="form-group">
            <label>Model id</label>
            <input
              type="text"
              value={form.custom_model}
              onChange={(e) => setForm({ ...form, custom_model: e.target.value })}
              placeholder="qwen2.5-1.5b-instruct"
            />
            <small>Sent as model_id to /v1/text/generate on your gateway host.</small>
          </div>
          <div className="form-actions" style={{ marginTop: 8 }}>
            <LoadingButton
              type="button"
              className="btn btn-secondary"
              loading={probeBusy}
              loadingLabel="Testing…"
              onClick={runCustomAiProbe}
              disabled={!form.providers_enabled.custom}
            >
              Test Custom AI connection
            </LoadingButton>
          </div>
          {probeResult && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 6,
                background: probeResult.ok ? '#e8f5e9' : '#fff3e0',
                border: `1px solid ${probeResult.ok ? '#a5d6a7' : '#ffcc80'}`,
                fontSize: 13,
                whiteSpace: 'pre-wrap',
              }}
            >
              <strong>{probeResult.ok ? 'Connection OK' : 'Connection failed'}</strong>
              {'\n'}
              {probeResult.hint || probeResult.error || ''}
              {'\n\n'}
              URL: {probeResult.url || '—'}
              {'\n'}
              Status: {probeResult.status ?? '—'}
              {'\n'}
              Client ID: {probeResult.client_id_preview || '—'}
              {probeResult.client_id_looks_like_access_token === false ? ' (missing .access suffix?)' : ''}
              {'\n'}
              Secret set: {probeResult.secret_set ? 'yes' : 'no'}
              {'\n'}
              Headers: {(probeResult.headers_sent || []).join(', ')}
              {probeResult.challenge_detected ? '\nChallenge page: yes (likely Bot Fight Mode)' : ''}
              {probeResult.access_login_redirect ? '\nAccess login redirect: yes' : ''}
              {probeResult.body_preview ? `\nPreview: ${probeResult.body_preview}` : ''}
            </div>
          )}
        </fieldset>

        <h3 style={{ marginTop: 28 }}>Action routing</h3>
        <p style={{ color: '#666', marginBottom: 12, fontSize: 14 }}>
          Enable providers above first, then choose which one runs each feature. Custom appears here only after you check Custom provider.
        </p>
        {AI_ACTION_KEYS.filter((actionKey) => actionKey !== 'resume_studio').map((actionKey) => {
          const selectedProvider = resolveActionProviderValue(
            form.action_providers,
            actionKey,
            enabledProviderIds
          );
          return (
            <div className="form-group" key={actionKey}>
              <label>{AI_ACTION_LABELS[actionKey]}</label>
              <select
                value={selectedProvider}
                onChange={(e) => setActionProvider(actionKey, e.target.value)}
                disabled={!enabledProviderIds.length}
              >
                {enabledProviderIds.map((providerId) => (
                  <option key={providerId} value={providerId}>
                    {AI_PROVIDER_LABELS[providerId]}
                  </option>
                ))}
              </select>
              {actionKey === 'job_rematch' ? (
                <p className="form-hint">Resume Builder score, review, and chat use this same provider, model, and key.</p>
              ) : null}
            </div>
          );
        })}

        <div className="form-actions">
          <LoadingButton type="submit" className="btn btn-primary" icon={FiSave} loading={saveMutation.isLoading} loadingLabel="Saving…">
            Save ATS match settings
          </LoadingButton>
        </div>
      </form>

      <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #eee' }}>
        <h3>
          <FiRefreshCw /> Recompute matches
        </h3>
        <p style={{ color: '#666' }}>
          Uses the provider assigned to <strong>{AI_ACTION_LABELS.job_rematch}</strong> (
          {AI_PROVIDER_LABELS[form.action_providers.job_rematch] || 'not set'}).
        </p>
        <LoadingButton
          type="button"
          className="btn btn-secondary"
          icon={FiRefreshCw}
          loading={recomputeBusy}
          loadingLabel="Running…"
          onClick={runRecomputeAll}
        >
          Recompute AI matches for all active jobs
        </LoadingButton>
        {recomputeLog && (
          <pre
            style={{
              marginTop: 12,
              fontSize: 12,
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 6,
              maxHeight: 240,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {recomputeLog}
          </pre>
        )}
      </div>
    </div>
  );
};

export default AiMatchingSettings;
